import {
  BLOCKED_TYPE_TEXT,
  BlockedCheaterFields,
  BlockedCourseChipGroup,
  calculateBlockedDuration,
  useGetBlockedRecordDownloadSignedUrlWithInfo,
} from '@exam-center/blocked-user-management';
import { BlockedRecordBlockedScope } from '@exam-center/graphql';
import { Text } from '@shared/components';
import { dayjs, formatDate } from '@shared/utils';
import { useMemo } from 'react';

import { useBlockedRecordDetails } from '../../providers';
import { getFilePreviewModeByType } from '../blocked-record-cheater-form-logic/utils';

export default function BlockedCheaterReadonlyDetails() {
  const { blockedRecord } = useBlockedRecordDetails();

  const blockedAt = blockedRecord.blockedAt;
  const blockedUntil = blockedRecord.blockedUntil;
  const blockedCourses = blockedRecord.blockedRecordCourses.map((course) => ({
    code: course.examCourse?.code ?? '',
    name: course.examCourse?.name ?? '',
  }));
  const { metadata } = blockedRecord;

  const isBlockedAllScope = blockedRecord.blockedScope === BlockedRecordBlockedScope.AllScope;

  const examStartedAt = dayjs(metadata.examSession?.startedAt);
  const examEndedAt = dayjs(metadata.examSession?.endedAt);

  const round = metadata.examSession?.roundNumber;
  const examDate = formatDate(metadata.examSession?.startedAt);
  const startTime = examStartedAt.format('HH:mm');
  const endTime = examEndedAt.format('HH:mm');

  const documentStorageObjects = useMemo(() => {
    return (
      blockedRecord.metadata.documentStorageObjects?.map((object) => ({
        fileName: object.fileName,
        key: object.key,
      })) ?? []
    );
  }, [blockedRecord.metadata.documentStorageObjects]);

  const evidenceFiles = useGetBlockedRecordDownloadSignedUrlWithInfo({
    blockedRecordId: blockedRecord.id,
    blockedRecordStorageObjects: documentStorageObjects,
    options: { skip: !blockedRecord },
  });

  const totalBlockedDurationText = useMemo(() => {
    if (!blockedAt || !blockedUntil) return '-';

    return calculateBlockedDuration(blockedAt, blockedUntil);
  }, [blockedAt, blockedUntil]);

  const examCenterName = useMemo(() => {
    return blockedRecord.examCenter?.code && blockedRecord.examCenter.nameTH
      ? `${blockedRecord.examCenter?.code}: ${blockedRecord.examCenter.nameTH}`
      : '-';
  }, [blockedRecord.examCenter]);

  return (
    <BlockedCheaterFields
      fields={{
        blockedType: {
          mode: 'view',
          value: BLOCKED_TYPE_TEXT[blockedRecord?.blockedType],
        },
        blockedCourseType: {
          mode: 'view',
          value: isBlockedAllScope ? (
            <Text>ระงับสิทธิ์ทุกหลักสูตร</Text>
          ) : (
            <BlockedCourseChipGroup examCourseInfos={blockedCourses} />
          ),
          slotProps: {
            icon: { sx: { display: 'none' } },
          },
        },
        startedAt: {
          mode: 'view',
          value: formatDate(blockedAt),
        },
        endedAt: {
          mode: 'view',
          value: blockedUntil ? formatDate(blockedUntil) : '-',
        },
        remark: {
          mode: 'view',
          value: blockedRecord?.blockedReason || '-',
        },
        examDate: {
          mode: 'view',
          value: examDate,
        },
        examRound: {
          mode: 'view',
          value: round ? round.toString() : '-',
        },
        examStartTime: {
          mode: 'view',
          value: startTime,
        },
        examEndTime: {
          mode: 'view',
          value: endTime,
        },
        evidenceFiles: {
          mode: 'view',
          slotProps: {
            files: evidenceFiles.map((file) => ({
              ...file,
              mode: getFilePreviewModeByType(file.type),
            })),
          },
        },
        examCenter: {
          mode: 'view',
          value: examCenterName,
        },
      }}
      options={{
        shouldDisplayEndedAtAsDate: true,
        shouldDisplayBlockedCourseSelection: false,
      }}
      slots={{
        endedAtDateEndAdornment: (
          <Text sx={(theme) => ({ pb: theme.spacing(3) })}>
            (รวมระยะเวลาระงับสิทธิ์ทั้งหมด: {totalBlockedDurationText})
          </Text>
        ),
        blockedAtLabelText: 'วันที่เริ่มระงับสิทธิ์',
        blockedUntilLabelText: 'วันที่สิ้นสุดระงับสิทธิ์',
      }}
    />
  );
}
